import { useProxy } from 'hooks/contracts/proxy/useProxy';
import { useIsProxyAvailableFlag } from 'hooks/flags/useIsProxyAvailableFlag';
import {
  TransactionAction,
  useWalletNotifications,
} from 'hooks/notifications/useWalletNotifications';
import { useEffect } from 'react';
import { useProxiedAddress } from 'recoil/proxiedAddress';
import { ContractFunction } from 'types/contracts/functions';
import { Address } from 'viem';
import {
  Config,
  UseWriteContractReturnType,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';

export interface UseContractHookProps {
  args?: unknown[];
  contractAbi: readonly object[];
  functionName: ContractFunction;
}

interface Props extends UseContractHookProps {
  action: TransactionAction;
  allowProxy?: boolean;
  contractAddress: Address;
  onSent?: () => void;
  onConfirmed?: () => void;
  onFetched?: () => void;
  onError?: (error: Error) => void;
}

export type UseWriteContractAndConfirmReturnType = Omit<
  UseWriteContractReturnType<Config, unknown>,
  'writeContract'
> & {
  writeContract: VoidFunction;
};

export function useWriteContractAndConfirm({
  action,
  args,
  allowProxy = false,
  contractAbi,
  contractAddress,
  functionName,
  onSent,
  onConfirmed,
  onFetched,
  onError,
}: Props): UseWriteContractAndConfirmReturnType {
  const { proxiedAddress } = useProxiedAddress();
  const isProxyAvailable = useIsProxyAvailableFlag();

  const {
    notifyTransactionSent,
    notifyTransactionError,
    notifyTransactionConfirmed,
  } = useWalletNotifications(action);

  const isUsingProxy = isProxyAvailable && allowProxy && !!proxiedAddress;

  const writeContractReturn = useWriteContract();
  const { writeContract, data, isPending, isError, error } =
    writeContractReturn;

  const write = (): void =>
    writeContract(
      {
        abi: contractAbi,
        address: contractAddress,
        functionName: functionName,
        args,
      },
      {
        onSuccess(hash) {
          notifyTransactionSent(hash);
          onSent?.();
        },
        onError(error) {
          notifyTransactionError(error);
        },
      },
    );

  const {
    data: proxyData,
    writeContract: proxyWrite,
    error: proxyError,
    isError: isProxyError,
    isPending: isPendingProxy,
  } = useProxy({
    args,
    contractAbi,
    proxiedAddress: proxiedAddress as Address,
    callToAddress: isUsingProxy ? contractAddress : undefined,
    functionName,
    onSent(hash) {
      notifyTransactionSent(hash);
      onSent?.();
    },
    onError(error) {
      notifyTransactionError(error);
      onError?.(error);
    },
  });

  const finalData = isUsingProxy ? proxyData : data;

  const {
    isSuccess,
    isError: isTransactionError,
    error: transactionError,
    isFetched,
    isFetching,
  } = useWaitForTransactionReceipt({
    hash: finalData,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isSuccess) {
      notifyTransactionConfirmed(finalData);
      onConfirmed?.();
    }
  }, [isSuccess, action, notifyTransactionConfirmed]);

  useEffect(() => {
    if (isTransactionError) {
      notifyTransactionError(transactionError);
    }
  }, [isTransactionError, transactionError, notifyTransactionError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isFetched) {
      onFetched?.();
    }
  }, [isFetched]);

  return {
    ...writeContractReturn,
    data: finalData,
    writeContract: isUsingProxy ? proxyWrite : write,
    isPending: isFetching || isPendingProxy || isPending,
    isSuccess,
    isError: isProxyError || isError,
    error: proxyError || error,
  };
}
